import React, {
  useEffect,
  useState,
} from "react";

import {
  Link,
  useParams,
} from "react-router-dom";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  motion,
  AnimatePresence,
} from "framer-motion";

import {
  useTranslation,
} from "react-i18next";

import axios from "axios";
import DOMPurify from "dompurify";

import {
  Header,
  Section,
} from "../components/layout";

import {
  Fotter,
} from "../components";

import {
  Loader,
} from "../components/utils";

import {
  useStateContext,
} from "../context/ContextProvider";

import {
  getMeal,
} from "../redux/actions/meals";

import {
  BaseUrl,
  mealUrl,
  media,
} from "../server";

import RelatedBigMeals from "../components/RelatedBigMeals";
import ShareMeal from "../components/utils/ShareMeal";
import AddToOrderModal from "../components/order/AddToOrderModal";

import {
  FiArrowLeft,
  FiArrowRight,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiCheck,
  FiShoppingBag,
  FiShare2,
} from "react-icons/fi";

import {
  AiFillLike,
  AiOutlineLike,
} from "react-icons/ai";

import {
  FaRegEye,
  FaUtensils,
} from "react-icons/fa";

import {
  BiRestaurant,
} from "react-icons/bi";


function MealDetails() {
  const dispatch = useDispatch();

  const { id } = useParams();

  const { t } =
    useTranslation();

  const {
    currentDir,
  } = useStateContext();

  const isRTL =
    currentDir === "rtl";


  /* =================================
     REDUX
  ================================= */

  const {
    mealData,
    isLoadingMeal,
  } = useSelector(
    (state) => state.Meals
  );


  /* =================================
     LOCAL STATES
  ================================= */

  const [
    like,
    setLike,
  ] = useState(false);

  const [
    likes,
    setLikes,
  ] = useState(0);

  const [
    orderModalOpen,
    setOrderModalOpen,
  ] = useState(false);

  const [
    showAddedMessage,
    setShowAddedMessage,
  ] = useState(false);


  /* =================================
     DATA
  ================================= */

  const title =
    isRTL
      ? mealData?.titleAr
      : mealData?.titleEn;

  const description =
    isRTL
      ? mealData?.textAr
      : mealData?.textEn;

  const categoryTitle =
    isRTL
      ? mealData?.categoryId?.titleAr
      : mealData?.categoryId?.titleEn;

  const DirectionArrow =
    isRTL
      ? FiArrowLeft
      : FiArrowRight;

  const BreadcrumbArrow =
    isRTL
      ? FiChevronLeft
      : FiChevronRight;

  const preparationTime =
    mealData?.preparationTime
      ? `${mealData.preparationTime} ${t(
          "minutes"
        )}`
      : `15-20 ${t("minutes")}`;


  /* =================================
     FETCH MEAL
  ================================= */

  useEffect(() => {
    dispatch(getMeal(id));

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [
    id,
    dispatch,
  ]);


  /* =================================
     UPDATE LIKES AFTER FETCH
  ================================= */

  useEffect(() => {
    if (mealData) {
      setLikes(
        mealData?.likes || 0
      );
    }
  }, [mealData]);


  /* =================================
     LIKE
  ================================= */

  const LikeHandler = async () => {
    if (like) return;

    const previousLikes =
      likes;

    setLikes(
      (prev) => prev + 1
    );

    setLike(true);

    try {
      const {
        data,
      } = await axios.get(
        `${BaseUrl}/blogs/like/${id}`
      );

      if (!data?.success) {
        setLikes(
          previousLikes
        );

        setLike(false);
      }
    } catch (error) {
      setLikes(
        previousLikes
      );

      setLike(false);

      console.log(
        error?.response?.data
          ?.message ||
          error.message
      );
    }
  };


  /* =================================
     ADDED TO ORDER
  ================================= */

  const handleAddedToOrder =
    () => {
      setShowAddedMessage(
        true
      );

      setTimeout(() => {
        setShowAddedMessage(
          false
        );
      }, 2500);
    };


  /* =================================
     ANIMATION
  ================================= */

  const containerVariants = {
    hidden: {
      opacity: 0,
    },

    visible: {
      opacity: 1,

      transition: {
        staggerChildren: 0.1,
      },
    },
  };


  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 25,
    },

    visible: {
      opacity: 1,
      y: 0,

      transition: {
        duration: 0.55,
      },
    },
  };


  return (
    <>
      <Header
        active={4}
        DropActive={1}
      />


      {/* =================================
          LOADING
      ================================= */}

      {isLoadingMeal ? (
        <div
          className="
            min-h-screen

            flex
            items-center
            justify-center

            bg-back-primery
            dark:bg-back-darkprimery
          "
        >
          <Loader />
        </div>
      ) : (

        <motion.main
          initial="hidden"
          animate="visible"
          variants={
            containerVariants
          }
          className="
            overflow-hidden

            bg-back-primery
            dark:bg-back-darkprimery
          "
        >

          {/* =================================
              HERO
          ================================= */}

          <section
            className="
              relative

              h-[500px]
              md:h-[580px]
              lg:h-[650px]

              overflow-hidden

              bg-color-2
            "
          >

            {/* Image */}

            <motion.img
              initial={{
                scale: 1.08,
                opacity: 0,
              }}

              animate={{
                scale: 1,
                opacity: 1,
              }}

              transition={{
                duration: 1,
              }}

              src={`${media}${mealData?.Image}`}

              alt={title}

              className="
                absolute
                inset-0

                w-full
                h-full

                object-cover
              "
            />


            {/* Global Overlay */}

            <div
              className="
                absolute
                inset-0

                bg-black/10
              "
            />


            {/* Directional Overlay */}

            <div
              className={`
                absolute
                inset-0

                ${
                  isRTL
                    ? `
                      bg-gradient-to-l

                      from-color-2/95
                      via-color-2/55
                      to-transparent
                    `
                    : `
                      bg-gradient-to-r

                      from-color-2/95
                      via-color-2/55
                      to-transparent
                    `
                }
              `}
            />


            {/* Bottom Overlay */}

            <div
              className="
                absolute
                inset-0

                bg-gradient-to-t

                from-color-2/80
                via-transparent
                to-black/10
              "
            />


            {/* Gold Glow */}

            <div
              className={`
                absolute

                top-20

                w-[340px]
                h-[340px]

                rounded-full

                bg-color-1/20

                blur-[130px]

                pointer-events-none

                ${
                  isRTL
                    ? "-right-40"
                    : "-left-40"
                }
              `}
            />


            {/* Hero Content */}

            <div
              className="
                relative
                z-10

                container

                h-full

                flex
                items-end

                pb-14
                md:pb-20
              "
            >
              <motion.div
                variants={
                  itemVariants
                }

                className="
                  max-w-[800px]
                "
              >

                {/* Category */}

                <div
                  className="
                    flex
                    items-center

                    gap-3

                    mb-5
                  "
                >
                  <span
                    className="
                      block

                      w-12
                      md:w-16

                      h-[2px]

                      bg-color-1
                    "
                  />

                  <FaUtensils
                    className="
                      text-color-1
                    "
                  />

                  <span
                    className="
                      font-kufi

                      text-xs
                      md:text-sm

                      text-color-1
                    "
                  >
                    {categoryTitle ||
                      t(
                        "Yaman dish"
                      )}
                  </span>
                </div>


                {/* Title */}

                <h1
                  className="
                    max-w-[800px]

                    font-kufi
                    font-bold

                    text-white

                    text-[2.5rem]
                    leading-[1.4]

                    md:text-5xl
                    md:leading-[1.4]

                    lg:text-[4rem]
                    lg:leading-[1.3]

                    mb-6
                  "
                >
                  {title}
                </h1>


                {/* Short description */}

                {description && (
                  <p
                    className="
                      max-w-2xl

                      font-kufi

                      text-sm
                      md:text-lg

                      text-white/75

                      leading-8
                      md:leading-9

                      line-clamp-2
                    "
                  >
                    {description.replace(
                      /<[^>]*>/g,
                      ""
                    )}
                  </p>
                )}


                {/* Quick meta */}

                <div
                  className="
                    flex
                    flex-wrap

                    items-center

                    gap-3

                    mt-8
                  "
                >

                  {/* Time */}

                  <div
                    className="
                      flex
                      items-center

                      gap-2

                      rounded-full

                      border
                      border-white/15

                      bg-white/10
                      backdrop-blur-md

                      px-4
                      py-2.5

                      text-white
                    "
                  >
                    <FiClock
                      className="
                        text-color-1
                      "
                    />

                    <span
                      className="
                        font-kufi
                        text-xs
                        md:text-sm
                      "
                    >
                      {
                        preparationTime
                      }
                    </span>
                  </div>


                  {/* Views */}

                  <div
                    className="
                      flex
                      items-center

                      gap-2

                      rounded-full

                      border
                      border-white/15

                      bg-white/10
                      backdrop-blur-md

                      px-4
                      py-2.5

                      text-white
                    "
                  >
                    <FaRegEye
                      className="
                        text-color-1
                      "
                    />

                    <span
                      className="
                        text-sm
                      "
                    >
                      {mealData?.views ||
                        0}
                    </span>
                  </div>

                </div>

              </motion.div>
            </div>


            {/* Accent */}

            <div
              className="
                absolute
                bottom-0
                start-0

                w-32
                md:w-52

                h-[3px]

                bg-color-1
              "
            />

          </section>


          {/* =================================
              BODY
          ================================= */}

          <Section>
            <section
              className="
                relative

                py-12
                md:py-16
                lg:py-20
              "
            >
              <div className="container">

                {/* =================================
                    BREADCRUMB
                ================================= */}

                <motion.div
                  variants={
                    itemVariants
                  }

                  className="
                    flex
                    flex-wrap

                    items-center

                    gap-2

                    mb-10

                    font-kufi
                    text-xs
                    md:text-sm

                    text-n-5
                    dark:text-tcolor-3
                  "
                >

                  <Link
                    to="/"
                    className="
                      transition-colors

                      hover:text-color-1
                    "
                  >
                    {t("home")}
                  </Link>


                  <BreadcrumbArrow
                    size={13}
                  />


                  <Link
                    to="/AllResturentMeals"

                    className="
                      transition-colors

                      hover:text-color-1
                    "
                  >
                    {t("all meals")}
                  </Link>


                  <BreadcrumbArrow
                    size={13}
                  />


                  <span
                    className="
                      text-color-1

                      line-clamp-1
                    "
                  >
                    {title}
                  </span>

                </motion.div>


                {/* =================================
                    MAIN LAYOUT
                ================================= */}

                <div
                  className="
                    grid

                    lg:grid-cols-[minmax(0,1fr)_380px]

                    gap-10
                    lg:gap-14

                    items-start
                  "
                >

                  {/* =================================
                      MAIN CONTENT
                  ================================= */}

                  <div
                    className="
                      min-w-0
                    "
                  >

                    {/* =============================
                        ACTION BAR
                    ============================== */}

                    <motion.div
                      variants={
                        itemVariants
                      }

                      className="
                        flex
                        flex-col

                        sm:flex-row
                        sm:items-center
                        sm:justify-between

                        gap-5

                        rounded-[1.5rem]

                        border
                        border-color-2/10
                        dark:border-white/10

                        bg-back-secondary
                        dark:bg-back-darksecondary

                        p-4
                        md:p-5

                        mb-10
                      "
                    >

                      {/* Stats */}

                      <div
                        className="
                          flex
                          flex-wrap

                          items-center

                          gap-5
                          md:gap-8
                        "
                      >

                        {/* Views */}

                        <div
                          className="
                            flex
                            items-center

                            gap-3
                          "
                        >
                          <div
                            className="
                              flex

                              w-10
                              h-10

                              items-center
                              justify-center

                              rounded-full

                              bg-color-1/10

                              text-color-1
                            "
                          >
                            <FaRegEye />
                          </div>

                          <div>
                            <span
                              className="
                                block

                                font-kufi
                                text-[10px]

                                text-n-5
                                dark:text-tcolor-3
                              "
                            >
                              {t(
                                "views"
                              )}
                            </span>

                            <strong
                              className="
                                text-sm

                                text-tcolor-4
                                dark:text-white
                              "
                            >
                              {mealData?.views ||
                                0}
                            </strong>
                          </div>
                        </div>


                        {/* Likes */}

                        <div
                          className="
                            flex
                            items-center

                            gap-3
                          "
                        >
                          <div
                            className="
                              flex

                              w-10
                              h-10

                              items-center
                              justify-center

                              rounded-full

                              bg-color-1/10

                              text-color-1
                            "
                          >
                            {like ? (
                              <AiFillLike />
                            ) : (
                              <AiOutlineLike />
                            )}
                          </div>

                          <div>
                            <span
                              className="
                                block

                                font-kufi
                                text-[10px]

                                text-n-5
                                dark:text-tcolor-3
                              "
                            >
                              {t(
                                "likes"
                              )}
                            </span>

                            <strong
                              className="
                                text-sm

                                text-tcolor-4
                                dark:text-white
                              "
                            >
                              {likes}
                            </strong>
                          </div>
                        </div>


                        {/* Time */}

                        <div
                          className="
                            flex
                            items-center

                            gap-3
                          "
                        >
                          <div
                            className="
                              flex

                              w-10
                              h-10

                              items-center
                              justify-center

                              rounded-full

                              bg-color-1/10

                              text-color-1
                            "
                          >
                            <FiClock />
                          </div>

                          <div>
                            <span
                              className="
                                block

                                font-kufi
                                text-[10px]

                                text-n-5
                                dark:text-tcolor-3
                              "
                            >
                              {t(
                                "Preparation time"
                              )}
                            </span>

                            <strong
                              className="
                                font-kufi

                                text-xs

                                text-tcolor-4
                                dark:text-white
                              "
                            >
                              {
                                preparationTime
                              }
                            </strong>
                          </div>
                        </div>

                      </div>


                      {/* Social actions */}

                      <div
                        className="
                          flex
                          items-center

                          gap-2
                        "
                      >

                        <motion.button
                          type="button"

                          whileTap={{
                            scale: 0.95,
                          }}

                          onClick={
                            LikeHandler
                          }

                          className={`
                            flex

                            min-h-[44px]

                            items-center

                            gap-2

                            rounded-full

                            px-4

                            font-kufi
                            text-xs

                            transition-all
                            duration-300

                            ${
                              like
                                ? `
                                  bg-color-1
                                  text-white
                                `
                                : `
                                  border
                                  border-color-2/10
                                  dark:border-white/10

                                  bg-back-primery
                                  dark:bg-back-darkprimery

                                  text-tcolor-4
                                  dark:text-white

                                  hover:text-color-1
                                `
                            }
                          `}
                        >
                          {like ? (
                            <AiFillLike />
                          ) : (
                            <AiOutlineLike />
                          )}

                          <span
                            className="
                              hidden
                              sm:inline
                            "
                          >
                            {like
                              ? t(
                                  "liked"
                                )
                              : t(
                                  "like"
                                )}
                          </span>
                        </motion.button>


                        <ShareMeal
                          url={`${mealUrl}${mealData?._id}`}

                          title={
                            title
                          }

                          description={
                            description
                          }
                        />

                      </div>

                    </motion.div>


                    {/* =================================
                        DESCRIPTION
                    ================================= */}

                    <motion.article
                      variants={
                        itemVariants
                      }
                    >

                      {/* Label */}

                      <div
                        className="
                          flex
                          items-center

                          gap-3

                          mb-4
                        "
                      >
                        <span
                          className="
                            w-10
                            h-[2px]

                            bg-color-1
                          "
                        />

                        <span
                          className="
                            font-kufi
                            text-xs

                            text-color-1
                          "
                        >
                          {t(
                            "about this dish"
                          )}
                        </span>
                      </div>


                      <h2
                        className="
                          font-kufi
                          font-bold

                          text-2xl
                          md:text-3xl

                          text-tcolor-4
                          dark:text-tcolor-1

                          mb-6
                        "
                      >
                        {t(
                          "description"
                        )}
                      </h2>


                      <div
                        className="
                          prose
                          prose-lg
                          dark:prose-invert

                          max-w-none

                          font-kufi
                          font-light

                          text-n-6
                          dark:text-tcolor-2

                          leading-9
                        "

                        dangerouslySetInnerHTML={{
                          __html:
                            DOMPurify.sanitize(
                              description ||
                                ""
                            ),
                        }}
                      />

                    </motion.article>


                    {/* =================================
                        INGREDIENTS
                    ================================= */}

                    {Array.isArray(
                      mealData?.ingredients
                    ) &&
                      mealData.ingredients
                        .length >
                        0 && (

                      <motion.section
                        variants={
                          itemVariants
                        }

                        className="
                          mt-12

                          pt-10

                          border-t
                          border-color-2/10
                          dark:border-white/10
                        "
                      >

                        <div
                          className="
                            flex
                            items-center

                            gap-3

                            mb-6
                          "
                        >
                          <BiRestaurant
                            className="
                              text-color-1
                            "
                          />

                          <h2
                            className="
                              font-kufi
                              font-bold

                              text-xl
                              md:text-2xl

                              text-tcolor-4
                              dark:text-tcolor-1
                            "
                          >
                            {t(
                              "ingredients"
                            )}
                          </h2>
                        </div>


                        <div
                          className="
                            flex
                            flex-wrap

                            gap-3
                          "
                        >
                          {mealData.ingredients.map(
                            (
                              ingredient,
                              index
                            ) => (

                              <motion.span
                                key={
                                  index
                                }

                                initial={{
                                  opacity: 0,
                                  scale: 0.9,
                                }}

                                whileInView={{
                                  opacity: 1,
                                  scale: 1,
                                }}

                                viewport={{
                                  once: true,
                                }}

                                transition={{
                                  delay:
                                    index *
                                    0.04,
                                }}

                                className="
                                  rounded-full

                                  border
                                  border-color-1/15

                                  bg-color-1/5

                                  px-4
                                  py-2.5

                                  font-kufi
                                  text-xs
                                  md:text-sm

                                  text-tcolor-4
                                  dark:text-white/80
                                "
                              >
                                {
                                  ingredient
                                }
                              </motion.span>

                            )
                          )}
                        </div>

                      </motion.section>
                    )}

                  </div>


                  {/* =================================
                      ORDER SIDEBAR
                  ================================= */}

                  <motion.aside
                    variants={
                      itemVariants
                    }

                    className="
                      lg:sticky
                      lg:top-28
                    "
                  >

                    {/* Main Order Card */}

                    <div
                      className="
                        relative

                        overflow-hidden

                        rounded-[2rem]

                        bg-color-2

                        p-6
                        md:p-7

                        text-white

                        shadow-[0_20px_60px_rgba(68,37,20,0.20)]
                      "
                    >

                      {/* Decoration */}

                      <div
                        className="
                          absolute

                          -top-24
                          -end-24

                          w-52
                          h-52

                          rounded-full

                          bg-color-1/15

                          blur-[70px]

                          pointer-events-none
                        "
                      />


                      <div
                        className="
                          relative
                          z-10
                        "
                      >

                        {/* Icon */}

                        <div
                          className="
                            flex

                            w-14
                            h-14

                            items-center
                            justify-center

                            rounded-2xl

                            bg-color-1

                            text-white
                            text-xl

                            mb-6
                          "
                        >
                          <FiShoppingBag />
                        </div>


                        <span
                          className="
                            block

                            font-kufi
                            text-xs

                            text-white/55

                            mb-2
                          "
                        >
                          {t(
                            "meal price"
                          )}
                        </span>


                        {/* Price */}

                        {mealData?.price ? (

                          <div
                            className="
                              flex
                              items-end

                              gap-2

                              mb-4
                            "
                          >
                            <span
                              className="
                                text-4xl
                                md:text-5xl

                                font-bold

                                text-color-1
                              "
                            >
                              {
                                mealData.price
                              }
                            </span>

                            <span
                              className="
                                font-kufi
                                text-sm

                                text-white/70

                                mb-1
                              "
                            >
                              {t(
                                "currency"
                              )}
                            </span>
                          </div>

                        ) : (

                          <p
                            className="
                              font-kufi
                              text-xl

                              text-color-1

                              mb-4
                            "
                          >
                            {t(
                              "today price"
                            )}
                          </p>

                        )}


                        <p
                          className="
                            font-kufi
                            text-xs

                            text-white/55

                            leading-6

                            mb-7
                          "
                        >
                          {t(
                            "choose quantity and add any notes before adding to your order"
                          )}
                        </p>


                        {/* Order Now */}

                        <motion.button
                          type="button"

                          whileHover={{
                            y: -2,
                          }}

                          whileTap={{
                            scale: 0.98,
                          }}

                          onClick={() =>
                            setOrderModalOpen(
                              true
                            )
                          }

                          className="
                            group

                            flex

                            w-full

                            items-center
                            justify-center

                            gap-3

                            rounded-full

                            bg-color-1

                            px-6
                            py-4

                            font-kufi
                            font-semibold

                            text-white

                            shadow-[0_12px_35px_rgba(165,119,58,.25)]

                            transition-colors
                            duration-300

                            hover:bg-white
                            hover:text-color-2
                          "
                        >
                          <FiShoppingBag />

                          {t(
                            "order now"
                          )}

                          <DirectionArrow
                            className="
                              transition-transform
                              duration-300

                              group-hover:translate-x-1
                            "
                          />
                        </motion.button>

                      </div>

                    </div>


                    {/* Preparation Card */}

                    <div
                      className="
                        mt-5

                        flex
                        items-center

                        gap-4

                        rounded-[1.5rem]

                        border
                        border-color-2/10
                        dark:border-white/10

                        bg-back-secondary
                        dark:bg-back-darksecondary

                        p-5
                      "
                    >
                      <div
                        className="
                          flex

                          w-12
                          h-12

                          shrink-0

                          items-center
                          justify-center

                          rounded-full

                          bg-color-1/10

                          text-color-1
                        "
                      >
                        <FiClock />
                      </div>

                      <div>
                        <span
                          className="
                            block

                            font-kufi
                            text-[10px]

                            text-n-5
                            dark:text-tcolor-3

                            mb-1
                          "
                        >
                          {t(
                            "Preparation time"
                          )}
                        </span>

                        <p
                          className="
                            font-kufi
                            font-semibold

                            text-sm

                            text-tcolor-4
                            dark:text-white
                          "
                        >
                          {
                            preparationTime
                          }
                        </p>
                      </div>
                    </div>


                    {/* Nutrition */}

                    {mealData?.nutrition && (

                      <div
                        className="
                          mt-5

                          rounded-[1.5rem]

                          border
                          border-color-2/10
                          dark:border-white/10

                          bg-back-secondary
                          dark:bg-back-darksecondary

                          p-5
                        "
                      >
                        <h3
                          className="
                            font-kufi
                            font-semibold

                            text-base

                            text-tcolor-4
                            dark:text-white

                            mb-4
                          "
                        >
                          {t(
                            "nutritional information"
                          )}
                        </h3>


                        <div
                          className="
                            space-y-3
                          "
                        >
                          {Object.entries(
                            mealData.nutrition
                          ).map(
                            ([
                              key,
                              value,
                            ]) => (

                              <div
                                key={
                                  key
                                }

                                className="
                                  flex
                                  items-center
                                  justify-between

                                  gap-4

                                  pb-3

                                  border-b
                                  last:border-b-0

                                  border-color-2/10
                                  dark:border-white/10
                                "
                              >
                                <span
                                  className="
                                    font-kufi
                                    text-xs

                                    text-n-5
                                    dark:text-tcolor-3
                                  "
                                >
                                  {t(
                                    key
                                  )}
                                </span>

                                <strong
                                  className="
                                    text-sm

                                    text-tcolor-4
                                    dark:text-white
                                  "
                                >
                                  {
                                    value
                                  }
                                </strong>
                              </div>

                            )
                          )}
                        </div>

                      </div>
                    )}

                  </motion.aside>

                </div>

              </div>
            </section>
          </Section>


          {/* =================================
              RELATED MEALS
          ================================= */}

          <motion.section
            initial={{
              opacity: 0,
              y: 30,
            }}

            whileInView={{
              opacity: 1,
              y: 0,
            }}

            viewport={{
              once: true,
            }}

            transition={{
              duration: 0.65,
            }}
          >
            <RelatedBigMeals
              currentMealId={id}
            />
          </motion.section>


          <Fotter />

        </motion.main>
      )}


      {/* =================================
          ADD TO ORDER MODAL
      ================================= */}

      <AddToOrderModal
        isOpen={
          orderModalOpen
        }

        onClose={() =>
          setOrderModalOpen(
            false
          )
        }

        meal={mealData}

        onAdded={
          handleAddedToOrder
        }
      />


      {/* =================================
          ADDED SUCCESS MESSAGE
      ================================= */}

      <AnimatePresence>
        {showAddedMessage && (

          <motion.div
            initial={{
              opacity: 0,
              y: 30,
              scale: 0.95,
            }}

            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
            }}

            exit={{
              opacity: 0,
              y: 20,
              scale: 0.95,
            }}

            className="
              fixed

              z-[1300]

              bottom-5
              md:bottom-8

              start-1/2
              -translate-x-1/2

              w-[calc(100%-2rem)]
              max-w-[420px]

              flex
              items-center

              gap-3

              rounded-full

              bg-color-2

              px-5
              py-4

              text-white

              shadow-[0_20px_60px_rgba(68,37,20,.30)]
            "
          >
            <div
              className="
                flex

                w-9
                h-9

                shrink-0

                items-center
                justify-center

                rounded-full

                bg-color-1

                text-white
              "
            >
              <FiCheck />
            </div>

            <div>
              <p
                className="
                  font-kufi
                  font-semibold

                  text-sm
                "
              >
                {t(
                  "added to order"
                )}
              </p>

              <span
                className="
                  font-kufi
                  text-[11px]

                  text-white/60
                "
              >
                {t(
                  "you can review your order from the header"
                )}
              </span>
            </div>

          </motion.div>

        )}
      </AnimatePresence>

    </>
  );
}


export default MealDetails;